import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { changePassword } from '../api/client';
import { ArrowLeft, Loader2 } from 'lucide-react';

export function ChangePasswordPage() {
  useDocumentTitle('Change Password');
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setIsSubmitting(true);
      await changePassword({ currentPassword, newPassword, confirmNewPassword });
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button onClick={() => navigate('/security')} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Security</span>
          </button>

          <h1 style={styles.title}>Change Password</h1>

          {success ? (
            <div style={styles.successCard}>
              <p style={styles.successText}>Password changed successfully. All sessions have been signed out.</p>
              <p style={styles.redirectText}>Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  style={styles.input}
                  required
                  minLength={8}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={styles.input}
                  required
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <button
                type="submit"
                style={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '24px',
  },
  form: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '8px',
  },
  successCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '32px 24px',
    textAlign: 'center' as const,
  },
  successText: {
    color: '#16A34A',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '8px',
  },
  redirectText: {
    color: colors.gray[600],
    fontSize: '14px',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
